import { Box } from '@mui/material'
import { useMemo, useState } from 'react'

import ShippingMethodGroupSelect, {
    shippingMethodGroupMapping,
} from 'views/CalculateEmissions/RFQ/components/ShippingMethodGroupSelect'
import ShippingMethodPicker from 'views/CalculateEmissions/RFQ/components/ShippingMethodPicker'
import { TransportModeListOption } from 'views/CalculateEmissions/RFQ/components/TransportModePicker'
import { TransportMode, TransportModeGroup } from 'views/CalculateEmissions/RFQ/utils/sharedLogic'

interface GroupedShippingMethodPickerProps {
    value: TransportMode
    onChange: (value: TransportMode) => void
    error?: string
}

function getGroup(transportMode: TransportMode): TransportModeGroup {
    for (const [group, modes] of Object.entries(shippingMethodGroupMapping)) {
        if (modes.includes(transportMode)) {
            return group as TransportModeGroup
        }
    }
    // TODO There is a PR that centralises this logic and brings type safety. For now throw
    // https://github.com/lune-climate/lune-fe/pull/1795
    throw new Error('should never happen')
}

const GroupedShippingMethodPicker = ({
    value,
    onChange,
    error,
}: GroupedShippingMethodPickerProps) => {
    const [selectedGroup, setSelectedGroup] = useState<TransportModeGroup | undefined>(
        getGroup(value),
    )
    const [selectedShippingMethod, setSelectedShippingMethod] = useState<TransportMode | undefined>(
        value,
    )

    // Get filtered options based on selected group
    const filteredOptions = useMemo((): TransportModeListOption[] => {
        if (!selectedGroup) {
            return []
        }

        const modes = shippingMethodGroupMapping[selectedGroup]
        return modes.map((mode) => ({
            value: mode,
            label: mode,
            group: '',
        }))
    }, [selectedGroup])

    const handleGroupChange = (group: TransportModeGroup | undefined) => {
        setSelectedGroup(group)
        // Clear selected shipping method when group changes
        setSelectedShippingMethod(undefined)
    }

    const handleShippingMethodChange = (
        shippingMethod: TransportMode | 'Select a column' | undefined,
    ) => {
        // Filter out 'Select a column' since we don't use it in this component
        const filteredMethod = shippingMethod === 'Select a column' ? undefined : shippingMethod
        setSelectedShippingMethod(filteredMethod)
        // If it's an actual new selection let's update the mapping
        if (filteredMethod && filteredMethod !== selectedShippingMethod) {
            onChange(filteredMethod)
        }
    }

    const selectedShippingMethodOption = useMemo((): TransportModeListOption | undefined => {
        if (!selectedShippingMethod) {
            return undefined
        }
        return {
            value: selectedShippingMethod,
            label: selectedShippingMethod,
            group: '',
        }
    }, [selectedShippingMethod])

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    width: '40%',
                    '> div': {
                        width: '100%',
                        marginBottom: 0,
                    },
                }}
            >
                <ShippingMethodGroupSelect
                    selectedGroup={selectedGroup}
                    onUpdate={handleGroupChange}
                    preSelectedShippingMethod={selectedShippingMethod}
                />
            </Box>
            <Box
                sx={{
                    width: '60%',
                    '> div': {
                        width: '100%',
                        marginBottom: 0,
                    },
                }}
            >
                <ShippingMethodPicker
                    error={error}
                    selectedGroup={selectedGroup}
                    items={filteredOptions}
                    onChange={handleShippingMethodChange}
                    value={selectedShippingMethodOption}
                />
            </Box>
        </Box>
    )
}

export default GroupedShippingMethodPicker
